import React, { useState, useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { ICellRendererParams } from "ag-grid-community";
import { IconWithTooltip } from "@/components/Common/Icons/IconFactory";
import Dialog from "@/components/Common/Dialog/Dialog";
import ConfirmationDialog from "@/components/Common/Dialog/ConfirmationDialog";
import { StyledButton, LoadingButton } from "@/components/Button";
import { CommodityThresholdApi } from "@/api/RiskThreshold/CommodityThresholdApi";
import { DefaultThresholdApi } from "@/api/RiskThreshold/DefaultThresholdApi";
import {
  CommodityThreshold,
  DefaultThreshold,
} from "@/interfaces/RiskThresholdTypes";
import Slider from "@/components/Common/Slider/Slider";
import { useAppSelector } from "@/store/hooks";
import { selectUserEmail } from "@/store/userSlice";
import {
  buildCostcoEmail,
  isDefaultThreshold,
  isCommodityThreshold,
  thresholdToIndex,
  indexToThresholdMin,
  calculateHighMax,
  calculateMediumMax,
} from "./utils";
import { DIALOG_TEXT, ICONS, TEST_IDS, CSS_CLASSES } from "./constants";

interface GridContext {
  onRefresh: () => void;
}

interface ActionCellRendererProps extends ICellRendererParams {
  data: CommodityThreshold | DefaultThreshold;
  context: GridContext;
}

interface ResetDialogProps {
  commodityName: string;
  commodityId: number;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

interface EditDialogProps {
  commodityName: string;
  commodityId: number | null;
  isDefaultThreshold: boolean;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  fallbackThreshold: CommodityThreshold | DefaultThreshold;
}

const ERROR_MESSAGES = {
  FAILED_TO_LOAD: "Failed to load threshold data",
} as const;

const LOADING_MESSAGES = {
  LOADING_DATA: "Loading threshold data...",
} as const;

/**
 * Helper to extract display name from threshold data
 */
const getThresholdDisplayName = (
  data: CommodityThreshold | DefaultThreshold,
): string => {
  if (isDefaultThreshold(data)) {
    return data.thresholdDefaultName || "Default Threshold";
  }
  if (isCommodityThreshold(data)) {
    return data.scmCommodityNm || "Unknown Commodity";
  }
  return "Unknown";
};

const ResetDialog: React.FC<ResetDialogProps> = ({
  commodityName,
  commodityId,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const username = useAppSelector(selectUserEmail);

  const handleConfirm = async (): Promise<void> => {
    const userEmail = buildCostcoEmail(username);

    await CommodityThresholdApi.resetCommodityThreshold(commodityId, {
      userName: userEmail,
    });

    // Call success callback to refresh grid
    onSuccess();

    // Close dialog
    onClose();
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <Dialog
      size="md"
      header={
        <div>
          <h2 className="text-xl font-semibold text-gray-900 leading-tight">
            {DIALOG_TEXT.RESET_CONFIRMATION_TITLE_TEMPLATE(commodityName)}
          </h2>
        </div>
      }
      headerClassName={CSS_CLASSES.DIALOG_HEADER}
      contentClassName={CSS_CLASSES.DIALOG_CONTENT}
      footer={
        <div className="flex flex-row justify-end w-full gap-3">
          <StyledButton
            text={DIALOG_TEXT.CANCEL_TEXT}
            className="min-w-[100px] px-6 py-2.5 text-blue-600 bg-white rounded-sm border border-blue-600 hover:bg-blue-50 transition-colors"
            onClick={onClose}
          />
          <LoadingButton
            text={DIALOG_TEXT.CONFIRM_TEXT}
            onClick={handleConfirm}
            className="min-w-[100px] px-6 py-2.5"
            showToast={true}
            toastMessages={{
              loading: `Resetting ${commodityName}...`,
              success: `Successfully reset ${commodityName} to defaults`,
              error: `Failed to reset ${commodityName}`,
            }}
            minLoadingDuration={500}
          />
        </div>
      }
      footerClassName={CSS_CLASSES.DIALOG_FOOTER}
      content={
        <div className="space-y-4">
          <p className="text-base text-gray-700 leading-relaxed">
            {DIALOG_TEXT.RESET_OVERWRITE_MSG_TEMPLATE(commodityName)}
          </p>
        </div>
      }
      onClose={onClose}
    />,
    document.body,
  );
};

const EditDialog: React.FC<EditDialogProps> = ({
  commodityName,
  commodityId,
  isDefaultThreshold: isDefault,
  isOpen,
  onClose,
  onSuccess,
  fallbackThreshold,
}) => {
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [defaultThresholds, setDefaultThresholds] =
    useState<DefaultThreshold | null>(null);
  const [currentThreshold, setCurrentThreshold] = useState<
    CommodityThreshold | DefaultThreshold | null
  >(fallbackThreshold);

  const username = useAppSelector(selectUserEmail);

  // Baseline indices — set once from the authoritative fetch result.
  // Stored as state so they update when the async fetch completes, but
  // do NOT change when the user moves the slider.
  const [initialLeftIdx, setInitialLeftIdx] = useState(
    thresholdToIndex(fallbackThreshold.mediumMin),
  );
  const [initialRightIdx, setInitialRightIdx] = useState(
    thresholdToIndex(fallbackThreshold.lowMin),
  );

  // Current slider handle positions, initialised from fallback.
  const [leftIdx, setLeftIdx] = useState(initialLeftIdx);
  const [rightIdx, setRightIdx] = useState(initialRightIdx);

  // Keep refs in sync so handleSave always reads the latest values
  // without requiring them in its closure deps.
  const leftIdxRef = useRef(leftIdx);
  const rightIdxRef = useRef(rightIdx);
  leftIdxRef.current = leftIdx;
  rightIdxRef.current = rightIdx;

  const hasChanges = leftIdx !== initialLeftIdx || rightIdx !== initialRightIdx;

  // Fetch threshold data when dialog opens
  useEffect(() => {
    const fetchThresholdData = async (): Promise<void> => {
      if (!isOpen) return;

      // Seed slider immediately from table-row data while fetch is in flight
      if (fallbackThreshold) {
        const leftIndex = thresholdToIndex(fallbackThreshold.mediumMin);
        const rightIndex = thresholdToIndex(fallbackThreshold.lowMin);
        setCurrentThreshold(fallbackThreshold);
        setInitialLeftIdx(leftIndex);
        setInitialRightIdx(rightIndex);
        setLeftIdx(leftIndex);
        setRightIdx(rightIndex);
      }

      setIsLoading(true);
      try {
        if (isDefault) {
          // Fetch default threshold
          const defaults = await DefaultThresholdApi.getActiveDefaults();
          const leftIndex = thresholdToIndex(defaults.mediumMin);
          const rightIndex = thresholdToIndex(defaults.lowMin);
          setCurrentThreshold(defaults);
          setDefaultThresholds(defaults);
          setInitialLeftIdx(leftIndex);
          setInitialRightIdx(rightIndex);
          setLeftIdx(leftIndex);
          setRightIdx(rightIndex);
        } else if (commodityId !== null) {
          // Fetch commodity threshold by ID
          const commodity =
            await CommodityThresholdApi.getCommodityThresholdById(commodityId);
          const leftIndex = thresholdToIndex(commodity.mediumMin);
          const rightIndex = thresholdToIndex(commodity.lowMin);
          setCurrentThreshold(commodity);
          setInitialLeftIdx(leftIndex);
          setInitialRightIdx(rightIndex);
          setLeftIdx(leftIndex);
          setRightIdx(rightIndex);

          // Always fetch latest defaults for comparison baseline
          const defaults = await DefaultThresholdApi.getActiveDefaults();
          setDefaultThresholds(defaults);
        } else {
          // No commodityId — use fallback; still load defaults for comparison
          if (isCommodityThreshold(fallbackThreshold)) {
            const defaults = await DefaultThresholdApi.getActiveDefaults();
            setDefaultThresholds(defaults);
          }
        }
      } catch {
        // On error keep using the fallback values already applied above
      } finally {
        setIsLoading(false);
      }
    };

    void fetchThresholdData();
  }, [isOpen, isDefault, commodityId, fallbackThreshold]);

  const handleSliderChange = useCallback(
    (newLeftIdx: number, newRightIdx: number): void => {
      setLeftIdx(newLeftIdx);
      setRightIdx(newRightIdx);
    },
    [],
  );

  const handleSave = async (): Promise<void> => {
    // Read from refs so we always use the latest slider position
    const mediumMin = indexToThresholdMin(leftIdxRef.current);
    const lowMin = indexToThresholdMin(rightIdxRef.current);

    // Calculate derived values
    const highMax = calculateHighMax(mediumMin);
    const mediumMax = calculateMediumMax(lowMin);

    const userEmail = buildCostcoEmail(username);

    const payload = {
      highMax,
      mediumMin,
      mediumMax,
      lowMin,
      userName: userEmail,
    };

    // Call appropriate API based on threshold type
    if (isDefault) {
      await DefaultThresholdApi.updateDefaults(payload);
    } else if (commodityId !== null) {
      await CommodityThresholdApi.upsertCommodityThreshold(
        commodityId,
        payload,
      );
    }

    onSuccess();
    onClose();
  };

  const handleCloseAttempt = (): void => {
    if (hasChanges) {
      setShowUnsavedDialog(true);
    } else {
      onClose();
    }
  };

  const handleConfirmClose = (): void => {
    setShowUnsavedDialog(false);
    onClose();
  };

  if (!isOpen) return null;

  // Comparison baseline: use fetched defaults when available; otherwise
  // fall back to the initial position (so no spurious "modified" indicator).
  const originalLeftIdx = defaultThresholds
    ? thresholdToIndex(defaultThresholds.mediumMin)
    : initialLeftIdx;
  const originalRightIdx = defaultThresholds
    ? thresholdToIndex(defaultThresholds.lowMin)
    : initialRightIdx;

  return (
    <>
      {ReactDOM.createPortal(
        <Dialog
          size="lg"
          isFullScreenEnabled={true}
          header={
            <div>
              <h2 className="text-xl font-semibold text-gray-900 leading-tight">
                {DIALOG_TEXT.EDIT_TITLE_TEMPLATE(commodityName)}
              </h2>
            </div>
          }
          headerClassName={CSS_CLASSES.DIALOG_HEADER}
          contentClassName={CSS_CLASSES.DIALOG_CONTENT_EDIT}
          footer={
            <div className="flex flex-row justify-end w-full gap-3">
              <StyledButton
                text={DIALOG_TEXT.CANCEL_TEXT}
                className="min-w-[100px] px-6 py-2.5 text-blue-600 bg-white rounded-sm border border-blue-600 hover:bg-blue-50 transition-colors"
                onClick={handleCloseAttempt}
                disabled={isLoading}
              />
              <LoadingButton
                text={DIALOG_TEXT.SAVE_TEXT}
                onClick={handleSave}
                className="min-w-[100px] px-6 py-2.5"
                disabled={!hasChanges || isLoading}
                showToast={true}
                toastMessages={{
                  loading: `Saving ${commodityName}...`,
                  success: `Successfully saved ${commodityName}`,
                  error: `Failed to save ${commodityName}`,
                }}
                minLoadingDuration={500}
              />
            </div>
          }
          footerClassName={CSS_CLASSES.DIALOG_FOOTER}
          content={
            <div className="w-full max-w-4xl mx-auto">
              {isLoading && !currentThreshold ? (
                <div className="flex items-center justify-center py-20">
                  <div className="text-gray-500">
                    {LOADING_MESSAGES.LOADING_DATA}
                  </div>
                </div>
              ) : currentThreshold ? (
                <Slider
                  onChange={handleSliderChange}
                  showComparison={hasChanges}
                  defaultLeftIdx={initialLeftIdx}
                  defaultRightIdx={initialRightIdx}
                  originalLeftIdx={originalLeftIdx}
                  originalRightIdx={originalRightIdx}
                />
              ) : (
                <div className="flex items-center justify-center py-20">
                  <div className="text-red-500">
                    {ERROR_MESSAGES.FAILED_TO_LOAD}
                  </div>
                </div>
              )}
            </div>
          }
          onClose={handleCloseAttempt}
        />,
        document.body,
      )}

      <ConfirmationDialog
        isOpen={showUnsavedDialog}
        title={DIALOG_TEXT.UNSAVED_TITLE}
        message={DIALOG_TEXT.UNSAVED_MESSAGE}
        confirmText={DIALOG_TEXT.DISCARD_TEXT}
        cancelText={DIALOG_TEXT.KEEP_EDITING_TEXT}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowUnsavedDialog(false)}
        variant="warning"
      />
    </>
  );
};

export const ActionCellRenderer: React.FC<ActionCellRendererProps> = (
  params,
) => {
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  const data = params.data;

  const isUsingDefaultFromRow = isCommodityThreshold(data)
    ? data.isUsingDefault
    : false;
  const [isUsingDefaultLocal, setIsUsingDefaultLocal] = useState(
    isUsingDefaultFromRow,
  );

  // Keep local state aligned with grid data after auto-refresh completes.
  useEffect(() => {
    setIsUsingDefaultLocal(isUsingDefaultFromRow);
  }, [isUsingDefaultFromRow]);

  // Use a ref so callbacks always call the latest onRefresh even after
  // AG Grid re-uses this renderer instance with a new params object.
  const onRefreshRef = useCallback(
    () => params.context?.onRefresh?.(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [params.context],
  );

  const handleRefresh = (): void => {
    onRefreshRef();
  };

  const handleResetSuccess = (): void => {
    setIsUsingDefaultLocal(true);
    handleRefresh();
  };

  const handleEditSuccess = (): void => {
    if (isCommodityThreshold(data)) {
      setIsUsingDefaultLocal(false);
    }
    handleRefresh();
  };

  const isPinnedRow = params.node.rowPinned === "top";

  if (!data) {
    return null;
  }

  const commodityName = getThresholdDisplayName(data);

  // Extract the correct commodity ID - use scmCommodityId for API calls
  const commodityId = isCommodityThreshold(data) ? data.scmCommodityId : null;

  const isDefault = isDefaultThreshold(data);

  return (
    <>
      <div className={`${CSS_CLASSES.FLEX_CENTER} gap-10`}>
        <IconWithTooltip
          iconName={ICONS.EDIT}
          onClick={() => setIsEditDialogOpen(true)}
          className={CSS_CLASSES.POINTER_CURSOR}
          data-testid={TEST_IDS.EDIT_THRESHOLD}
        />
        {!isPinnedRow && (
          <IconWithTooltip
            iconName={ICONS.RESET}
            size={20}
            isDefaultValue={isUsingDefaultLocal}
            onClick={() => setIsResetDialogOpen(true)}
            className={CSS_CLASSES.POINTER_CURSOR}
            data-testid={TEST_IDS.RESET_THRESHOLD}
          />
        )}
      </div>

      {commodityId !== null && (
        <ResetDialog
          commodityName={commodityName}
          commodityId={commodityId}
          isOpen={isResetDialogOpen}
          onClose={() => setIsResetDialogOpen(false)}
          onSuccess={handleResetSuccess}
        />
      )}

      <EditDialog
        commodityName={commodityName}
        commodityId={commodityId}
        isDefaultThreshold={isDefault}
        isOpen={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        onSuccess={handleEditSuccess}
        fallbackThreshold={data}
      />
    </>
  );
};
